/**
 * Utilitários para normalização e resolução de categorias de transações.
 */

const STANDARD_PLATFORM_MAP = {
    'uber': 'Uber',
    '99': '99',
    'indrive': 'inDrive',
    'particular': 'Particular',
    'ifood': 'iFood',
    'rappi': 'Rappi',
    'zedelivery': 'Zé Delivery',
    'zé delivery': 'Zé Delivery',
    'lalamove': 'Lalamove',
    'loggi': 'Loggi',
    'outros': 'Outros'
};

/**
 * Resolve a categoria correta para uma plataforma, casando com as categorias registradas no app
 * @param {string} platformId - ID da plataforma (ex: 'uber', '99', 'indrive')
 * @param {string} platformName - Nome de exibição da plataforma (ex: 'Uber', '99', 'InDrive')
 * @param {Array<string>} incomeCategories - Lista de categorias de receita cadastradas
 * @returns {string} - Nome exato da categoria para salvar na transação
 */
export const resolvePlatformCategory = (platformId = '', platformName = '', incomeCategories = []) => {
    const platIdClean = String(platformId || '').trim();
    const platNameClean = String(platformName || '').trim();

    if (Array.isArray(incomeCategories) && incomeCategories.length > 0) {
        // Tenta encontrar uma correspondência exata (case-insensitive) nas categorias ativas
        const match = incomeCategories.find(cat => {
            const catLower = String(cat).toLowerCase();
            return (
                (platIdClean && catLower === platIdClean.toLowerCase()) ||
                (platNameClean && catLower === platNameClean.toLowerCase())
            );
        });
        if (match) return match;
    }

    // Procura no mapa padrão conhecido
    const lowerKey = platIdClean.toLowerCase();
    if (STANDARD_PLATFORM_MAP[lowerKey]) {
        return STANDARD_PLATFORM_MAP[lowerKey];
    }

    // Fallback inteligente
    if (platNameClean) {
        if (platNameClean.toLowerCase() === 'indrive') return 'inDrive';
        return platNameClean;
    }

    if (platIdClean.toLowerCase() === 'indrive') return 'inDrive';
    return platIdClean.charAt(0).toUpperCase() + platIdClean.slice(1);
};

/**
 * Sanitiza e normaliza uma transação, garantindo categoria correta,
 * valores com precisão monetária de 2 casas decimais e integridade de campos.
 * @param {Object} transaction
 * @param {Array<string>} [incomeCategories]
 * @returns {Object}
 */
export const normalizeTransaction = (transaction, incomeCategories = []) => {
    if (!transaction || typeof transaction !== 'object') return transaction;

    let category = transaction.category;
    const isCategoryEmpty = !category || category === '' || category === 'undefined' || category === 'null';

    // Se categoria está ausente em ganho, tenta recuperar via platform ou description
    if (isCategoryEmpty) {
        const plat = String(transaction.platform || '').trim();
        const desc = String(transaction.description || '').trim();

        if (/uber/i.test(plat) || /ganhos?\s+uber/i.test(desc)) category = 'Uber';
        else if (/99/i.test(plat) || /ganhos?\s+99/i.test(desc)) category = '99';
        else if (/indrive/i.test(plat) || /ganhos?\s+indrive/i.test(desc)) category = 'inDrive';
        else if (/particular/i.test(plat) || /ganhos?\s+particular/i.test(desc)) category = 'Particular';
        else if (/ifood/i.test(plat) || /ganhos?\s+ifood/i.test(desc)) category = 'iFood';
        else if (/rappi/i.test(plat) || /ganhos?\s+rappi/i.test(desc)) category = 'Rappi';
        else if (/z[eé]\s*delivery/i.test(plat) || /ganhos?\s+z[eé]\s*delivery/i.test(desc)) category = 'Zé Delivery';
        else if (/lalamove/i.test(plat) || /ganhos?\s+lalamove/i.test(desc)) category = 'Lalamove';
        else if (/loggi/i.test(plat) || /ganhos?\s+loggi/i.test(desc)) category = 'Loggi';
        else if (plat) {
            category = resolvePlatformCategory(plat, plat, incomeCategories);
        } else if (transaction.type === 'income') {
            category = 'Uber';
        }
    }

    // Normaliza variações de case para categorias conhecidas
    if (category === 'InDrive') {
        category = 'inDrive';
    }

    // Arredonda precisão de ponto flutuante em amount e originalAmount
    let amount = transaction.amount;
    if (typeof amount === 'number' && !isNaN(amount)) {
        amount = Math.round(amount * 100) / 100;
    }

    let originalAmount = transaction.originalAmount;
    if (typeof originalAmount === 'number' && !isNaN(originalAmount)) {
        originalAmount = Math.round(originalAmount * 100) / 100;
    }

    return {
        ...transaction,
        category: category || (transaction.type === 'income' ? 'Uber' : transaction.category),
        amount,
        ...(originalAmount !== undefined ? { originalAmount } : {})
    };
};
